/**
 * Phase 5 — Regular Restaurants table.
 *
 * One row per regular restaurant (from `meal_plan.regular_restaurants[]`,
 * pre-populated by the backend skeleton builder on voyage create).
 *
 * Each row has TWO sides — Lunch + Dinner — each with:
 *   - OPEN/CLOSED toggle (when CLOSED the side greys out + shows "CLOSED")
 *   - Menu select (drawn from the lib restaurant's `lunch_menus` /
 *     `dinner_menus`)
 *   - Dishes textarea (one per line)
 *   - When `has_dessert_rotation`: dessert menu select + dessert textarea
 *
 * Plus an "Activate Chef's Special" toggle per row. When ON the row gets a
 * gold border and a dropdown (`lib_chefs_specials` + Custom…). Stored on
 * `meal_plan.regular_restaurants[i].chefs_special`.
 */
import { useMemo, useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Award } from "lucide-react";
import { cn } from "@/lib/utils";
import { arrToText, textToArr } from "@/components/mealplan/textListHelpers";
import { isRowMenuDuplicate } from "@/components/mealplan/menuDuplicates";
import { isRowChefSpecialDuplicate } from "@/components/mealplan/chefSpecialDuplicates";
import FestivalsEditor from "@/components/mealplan/FestivalsEditor";

const NONE = "__none__";

// Phase 16 (BLOCKER 1) — menu rotations on `lib_restaurants.<meal>_menus`
// were widened from `List[str]` to `List[MenuEntry]` ({id, name, …}) in
// F8/CO3 but this table still iterates them as strings. Rendering an
// object as a React child crashes the day editor with
// "Objects are not valid as a React child" the moment any restaurant
// in the visible day has a non-empty menu rotation. Coerce defensively
// for every render path so legacy str entries also keep working.
const _menuName = (m) => {
  if (m == null) return "";
  if (typeof m === "string") return m;
  if (typeof m === "object") return (m.name ?? "").toString();
  return String(m);
};
const _coerceMenuList = (arr) => {
  if (!Array.isArray(arr)) return [];
  const out = [];
  const seen = new Set();
  for (const m of arr) {
    const n = _menuName(m).trim();
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

function ChefSpecialControl({
  value, onChange, specialsLib, restaurantId,
  isDuplicate, otherDupDays, sameDayCount,
  // Phase 16 (F11/CO4) — Chef's special groups for the optional filter
  // dropdown above the picker. When `null` or empty, the filter row
  // is hidden so the existing single-dropdown UI is unchanged.
  chefsSpecialsGroupsLib,
}) {
  const v = value || { enabled: false, chefs_special_id: null, name: "", group_id: null };
  const initialMode = v.chefs_special_id ? "select" : v.name ? "custom" : "select";
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    if (v.chefs_special_id) setMode("select");
    else if (v.name) setMode("custom");
  }, [v.chefs_special_id, v.name]);

  if (!v.enabled) return null;

  // Phase 16 (F11/CO4) — apply the group filter to the picker list.
  const effGroup = v.group_id || null;
  const filteredSpecials = (specialsLib || []).filter((cs) => {
    if (!effGroup) return true;
    return (cs.group_id || null) === effGroup;
  });

  const pick = (id) => {
    if (id === NONE) return onChange({ ...v, chefs_special_id: null, name: "" });
    if (id === "__other__") {
      setMode("custom");
      return onChange({ ...v, chefs_special_id: null, name: "" });
    }
    const cs = (specialsLib || []).find((s) => s.id === id);
    onChange({ ...v, chefs_special_id: id, name: cs?.name || "" });
  };
  const pickGroup = (gid) => {
    const next = gid === "__all__" ? null : gid;
    // Clear current pick if it falls outside the new group.
    if (next && v.chefs_special_id) {
      const cs = (specialsLib || []).find((s) => s.id === v.chefs_special_id);
      if (cs && (cs.group_id || null) !== next) {
        onChange({ ...v, group_id: next, chefs_special_id: null, name: "" });
        return;
      }
    }
    onChange({ ...v, group_id: next });
  };

  // Phase 7+++ — combined tooltip covering same-day + cross-day cases.
  let tooltip;
  if (isDuplicate) {
    const lines = [];
    if (sameDayCount >= 2) {
      lines.push("Same Chef's Special used on another restaurant today");
    }
    if (otherDupDays && otherDupDays.length > 0) {
      lines.push(
        "Same Chef's Special also used on " +
        otherDupDays.map((n) => `Day ${n}`).join(", "),
      );
    }
    tooltip = lines.length > 0
      ? lines.join(" · ")
      : "Duplicate Chef's Special";
  }

  return (
    <div
      // When duplicate, swap amber for red so the user gets an unmissable
      // visual signal alongside the native title-tooltip below.
      className={cn(
        "mt-2 rounded-md border px-2 py-1.5",
        isDuplicate
          ? "border-2 border-red-500 bg-red-50 ring-1 ring-red-200"
          : "border-amber-300 bg-amber-50",
      )}
      data-duplicate={isDuplicate ? "true" : undefined}
      data-testid={`regrest-chef-pill-${restaurantId}`}
      title={tooltip}
    >
      <div className="flex items-center gap-1.5">
        <Award
          className={cn(
            "h-3 w-3",
            isDuplicate ? "text-red-700" : "text-amber-700",
          )}
        />
        <span
          className={cn(
            "text-[10px] font-bold uppercase tracking-wider",
            isDuplicate ? "text-red-900" : "text-amber-900",
          )}
        >
          {isDuplicate ? "Duplicate · Chef's Special" : "Chef's Special"}
        </span>
      </div>
      {mode === "select" ? (
        <>
          {/* Phase 16 (F11/CO4) — optional group filter row. Renders
              only when groups library has entries. */}
          {(chefsSpecialsGroupsLib || []).length > 0 && (
            <Select value={effGroup || "__all__"} onValueChange={pickGroup}>
              <SelectTrigger
                className="mt-1 h-6 text-[10px]"
                data-testid={`regrest-chef-group-${restaurantId}`}
              >
                <SelectValue placeholder="Group filter…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">— All groups —</SelectItem>
                {(chefsSpecialsGroupsLib || []).map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.color && (
                      <span
                        className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                        style={{ backgroundColor: g.color }}
                      />
                    )}
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={v.chefs_special_id || ""} onValueChange={pick}>
            <SelectTrigger
              className="mt-1 h-7 text-xs"
              data-testid={`regrest-chef-select-${restaurantId}`}
            >
              <SelectValue placeholder="Pick a special…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— None —</SelectItem>
              {filteredSpecials.length === 0 ? (
                <div className="px-2 py-1 text-[9px] italic text-muted-foreground">
                  No matching specials in this group.
                </div>
              ) : (
                filteredSpecials.map((cs) => (
                  <SelectItem key={cs.id} value={cs.id}>{cs.name}</SelectItem>
                ))
              )}
              <SelectItem value="__other__">Other…</SelectItem>
            </SelectContent>
          </Select>
        </>
      ) : (
        <div className="mt-1 flex gap-1.5">
          <Input
            value={v.name || ""}
            onChange={(e) => onChange({ ...v, chefs_special_id: null, name: e.target.value })}
            placeholder="Custom dish"
            className="h-7 text-xs"
            data-testid={`regrest-chef-custom-${restaurantId}`}
          />
          <button
            type="button"
            onClick={() => { setMode("select"); onChange({ ...v, chefs_special_id: null, name: "" }); }}
            className="text-[10px] text-muted-foreground hover:underline"
          >List</button>
        </div>
      )}
    </div>
  );
}

function SidePanel({ row, side, restaurant, onPatch, isMenuDup, otherDupDays, menuListKey, dessertListKey, label }) {
  const isOpen = row[`${side}_status`] === "open";
  // Phase 7 — embarkation_lunch reuses the lunch menu list because it's
  // the same restaurant just being served at a different time. `menuListKey`
  // / `dessertListKey` lets the caller override which lib field to read.
  const menuField = menuListKey || side;
  const dessertField = dessertListKey || side;
  // Phase 16 (BLOCKER 1) — coerce menu rotations to a clean string list.
  // `lib_restaurants.<meal>_menus` may now hold MenuEntry objects post
  // F8/CO3 migration; rendering the raw value would crash with
  // "Objects are not valid as a React child".
  const menuList = _coerceMenuList(restaurant[`${menuField}_menus`]);
  const dessertList = restaurant.has_dessert_rotation
    ? _coerceMenuList(restaurant[`${dessertField}_dessert_menus`])
    : [];

  // Phase-5 ref 5 — duplicate menu rotation tooltip text. Renders something
  // like "Same menu also picked on Day 3, Day 7" so the user can navigate
  // to the conflicting days.
  const dupTooltip =
    isMenuDup && otherDupDays && otherDupDays.length > 0
      ? `Same menu also picked on ${otherDupDays.map((n) => `Day ${n}`).join(", ")}`
      : undefined;

  // B5 — when ACTIVATING a meal (CLOSED → OPEN), if the per-day start/end
  // are empty pull library `default_times.<menuField>.start/end`. Library
  // defaults NEVER overwrite an existing user value. Deactivating
  // (OPEN → CLOSED) does NOT clear times so toggling ON later restores.
  const handleToggleStatus = () => {
    if (isOpen) {
      onPatch({ [`${side}_status`]: "closed" });
      return;
    }
    const patch = { [`${side}_status`]: "open" };
    const lib = (restaurant && restaurant.default_times) || {};
    const t = lib[menuField] || {};
    const curStart = (row[`${side}_start`] ?? "").toString().trim();
    const curEnd = (row[`${side}_end`] ?? "").toString().trim();
    if (!curStart && t.start) patch[`${side}_start`] = t.start;
    if (!curEnd && t.end) patch[`${side}_end`] = t.end;
    onPatch(patch);
  };

  return (
    <td
      className={cn(
        "border-l p-2 align-top transition-colors",
        !isOpen && "bg-gray-100/60"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleToggleStatus}
          data-testid={`regrest-${side}-status-${restaurant.id}`}
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors",
            isOpen
              ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
              : "bg-red-100 text-red-700 hover:bg-red-200"
          )}
        >
          {isOpen ? "Open" : "Closed"}
        </button>
        {menuList.length > 0 && isOpen && (
          <Select
            value={row[`${side}_menu_name`] || ""}
            onValueChange={(v) =>
              onPatch({ [`${side}_menu_name`]: v === NONE ? null : v })
            }
          >
            <SelectTrigger
              className={cn(
                "h-7 max-w-[180px] flex-1 text-xs transition-colors",
                // Red ring + light-red fill when this menu rotation
                // duplicates another day's pick for the same restaurant +
                // side. data-duplicate so playwright + the PDF coverage
                // can detect it without colour-only checks.
                isMenuDup &&
                  "border-2 border-red-500 bg-red-50 ring-1 ring-red-200 text-red-900 font-medium",
              )}
              title={dupTooltip}
              data-duplicate={isMenuDup ? "true" : undefined}
              data-testid={`regrest-${side}-menu-${restaurant.id}`}
            >
              <SelectValue placeholder="Menu…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— None —</SelectItem>
              {menuList.map((mn) => (
                <SelectItem key={mn} value={mn}>{mn}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Hotfix 3 — inline START / END time inputs, mirror the Specialty
          UX. Only visible when the meal is OPEN; saves to the per-day
          venue snapshot (`<side>_start` / `<side>_end`). Library
          defaults still seed Day 1 via mealPlanSaveHelpers; editing
          these here only overrides this one day. */}
      {isOpen && (
        <div
          className="mt-1.5 flex items-center gap-1.5"
          data-testid={`regrest-${side}-times-${restaurant.id}`}
        >
          <input
            type="time"
            value={row[`${side}_start`] || ""}
            onChange={(e) =>
              onPatch({ [`${side}_start`]: e.target.value || null })
            }
            className="h-6 w-[72px] rounded border border-input bg-background px-1 text-[10px] tabular-nums"
            aria-label={`${label || side} start time`}
            data-testid={`regrest-${side}-start-${restaurant.id}`}
          />
          <span className="text-[10px] text-muted-foreground">→</span>
          <input
            type="time"
            value={row[`${side}_end`] || ""}
            onChange={(e) =>
              onPatch({ [`${side}_end`]: e.target.value || null })
            }
            className="h-6 w-[72px] rounded border border-input bg-background px-1 text-[10px] tabular-nums"
            aria-label={`${label || side} end time`}
            data-testid={`regrest-${side}-end-${restaurant.id}`}
          />
        </div>
      )}

      {isOpen ? (
        <>
          <Textarea
            value={arrToText(row[`${side}_dishes`])}
            onChange={(e) =>
              onPatch({ [`${side}_dishes`]: textToArr(e.target.value) })
            }
            placeholder="One dish per line"
            rows={3}
            className="mt-1.5 min-h-[60px] text-xs"
            data-testid={`regrest-${side}-dishes-${restaurant.id}`}
          />

          {restaurant.has_dessert_rotation && (
            <div className="mt-1.5 rounded-md border border-yellow-200 bg-yellow-50/60 p-1.5">
              {dessertList.length > 0 && (
                <Select
                  value={row[`${side}_dessert_menu_name`] || ""}
                  onValueChange={(v) =>
                    onPatch({ [`${side}_dessert_menu_name`]: v === NONE ? null : v })
                  }
                >
                  <SelectTrigger
                    className="h-7 text-xs"
                    data-testid={`regrest-${side}-dessert-menu-${restaurant.id}`}
                  >
                    <SelectValue placeholder="Dessert menu…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— None —</SelectItem>
                    {dessertList.map((mn) => (
                      <SelectItem key={mn} value={mn}>{mn}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Textarea
                value={arrToText(row[`${side}_desserts`])}
                onChange={(e) =>
                  onPatch({ [`${side}_desserts`]: textToArr(e.target.value) })
                }
                placeholder="Desserts (one per line)"
                rows={2}
                className="mt-1 min-h-[40px] text-xs"
                data-testid={`regrest-${side}-dessert-${restaurant.id}`}
              />
            </div>
          )}
        </>
      ) : (
        <div className="mt-3 flex items-center justify-center rounded-md border border-dashed border-gray-300 py-6">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            Closed
          </span>
        </div>
      )}
    </td>
  );
}

export default function RegularRestaurantsTable({
  value, onChange, restaurantsLib, chefsSpecialsLib,
  // Phase 16 (F11/CO4) — chef's special groups list for the optional
  // filter dropdown above each per-restaurant chef's-special picker.
  chefsSpecialsGroupsLib,
  // Phase-5 ref 5 — voyage-wide duplicate menu rotation map +
  // currently active day number for tooltip text.
  menuDuplicates, currentDayNumber,
  // Phase 7++ — festivals library + venue opt-in (lib.hosts_festivals).
  festivalsLib,
  // Phase 7+++ — voyage-wide duplicate Chef's Special map. Same shape
  // as menuDuplicates but scoped to chefs_special.name only.
  chefSpecialDuplicates,
}) {
  // Build lookup of restaurants by id for menu/dessert menu lookups + name.
  const libById = useMemo(() => {
    const m = {};
    for (const r of restaurantsLib || []) m[r.id] = r;
    return m;
  }, [restaurantsLib]);

  const rows = useMemo(() => value || [], [value]);

  // Phase 7+ — optional service columns. Each is rendered only when AT
  // LEAST ONE row's lib doc declares the menus for it. Most ships only
  // serve buffet breakfast/brunch/tea-time, so these stay collapsed by
  // default.
  const hasBreakfastColumn = useMemo(
    () => rows.some((r) => (libById[r.restaurant_id]?.breakfast_menus || []).length > 0),
    [rows, libById],
  );
  const hasBrunchColumn = useMemo(
    () => rows.some((r) => (libById[r.restaurant_id]?.brunch_menus || []).length > 0),
    [rows, libById],
  );
  const hasTeaTimeColumn = useMemo(
    () => rows.some((r) => (libById[r.restaurant_id]?.tea_time_menus || []).length > 0),
    [rows, libById],
  );
  // Phase 7 / Phase 13 — Embarkation columns are Day-1 only.
  const isEmbarkDay = Number(currentDayNumber) === 1;
  const colSpan =
    2
    + (hasBreakfastColumn ? 1 : 0)
    + (hasBrunchColumn ? 1 : 0)
    + (hasTeaTimeColumn ? 1 : 0)
    // Phase 13 — Embark Breakfast / Lunch / Dinner each add a column
    // on Day 1.
    + (isEmbarkDay ? 3 : 0);

  // Phase 7+++ — voyage-wide Chef's Special duplicate lookup. Replaces
  // the old same-day-only `duplicateNames` set computed from this day's
  // rows. The parent passes `chefSpecialDuplicates` derived from the full
  // voyage meal plan, so reuses across days are flagged red too.
  const chefDupInfo = (row) =>
    isRowChefSpecialDuplicate(
      chefSpecialDuplicates, row, currentDayNumber,
    );

  const patchRow = (rid, patch) => {
    onChange(
      rows.map((r) =>
        r.restaurant_id === rid ? { ...r, ...patch } : r
      )
    );
  };

  return (
    <div
      className="overflow-x-auto rounded-md border bg-white"
      data-testid="regular-restaurants-table"
    >
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-100">
            <th className="border-b px-3 py-2 text-left font-bold uppercase tracking-wider">
              Restaurant
            </th>
            {hasBreakfastColumn && (
              <th
                className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider"
                data-testid="regrest-header-breakfast"
              >
                Breakfast
              </th>
            )}
            {isEmbarkDay && (
              <th
                className="border-b border-l bg-amber-100 px-3 py-2 text-left font-bold uppercase tracking-wider text-amber-900"
                data-testid="regrest-header-embark-breakfast"
              >
                Embarkation Breakfast
              </th>
            )}
            {hasBrunchColumn && (
              <th
                className="border-b border-l bg-sky-50 px-3 py-2 text-left font-bold uppercase tracking-wider text-sky-900"
                data-testid="regrest-header-brunch"
              >
                Brunch
              </th>
            )}
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider">
              Lunch
            </th>
            {isEmbarkDay && (
              <th
                className="border-b border-l bg-amber-100 px-3 py-2 text-left font-bold uppercase tracking-wider text-amber-900"
                data-testid="regrest-header-embark-lunch"
              >
                Embarkation Lunch
              </th>
            )}
            {hasTeaTimeColumn && (
              <th
                className="border-b border-l bg-emerald-50 px-3 py-2 text-left font-bold uppercase tracking-wider text-emerald-900"
                data-testid="regrest-header-tea-time"
              >
                Tea Time
              </th>
            )}
            {isEmbarkDay && (
              <th
                className="border-b border-l bg-amber-100 px-3 py-2 text-left font-bold uppercase tracking-wider text-amber-900"
                data-testid="regrest-header-embark-dinner"
              >
                Embarkation Dinner
              </th>
            )}
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider">
              Dinner
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.flatMap((row) => {
            const lib = libById[row.restaurant_id];
            if (!lib) {
              // Phase 7 — defensive: don't drop the row when the library doc
              // has been deleted/replaced. Render a stale-data warning with
              // the snapshotted name so the user can see they need to fix
              // the library reference or re-create the row.
              return [
                <tr
                  key={row.restaurant_id}
                  data-testid={`regrest-row-${row.restaurant_id}`}
                  className="border-b bg-amber-50/40 last:border-b-0"
                >
                  <td colSpan={colSpan} className="px-3 py-3 align-top">
                    <div className="font-semibold text-amber-900">
                      {row.restaurant_name || "(unnamed)"}{" "}
                      <span className="ml-1 inline-flex items-center rounded-full bg-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900">
                        Stale lib reference
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-amber-800">
                      This row points at a restaurant that's no longer in
                      the library (id <code>{row.restaurant_id.slice(0, 8)}…</code>).
                      Re-create it in Settings → Restaurants, then remove +
                      re-add this row.
                    </p>
                  </td>
                </tr>,
              ];
            }
            const libHasBreakfast = (lib.breakfast_menus || []).length > 0;
            return [
              <tr
                key={row.restaurant_id}
                data-testid={`regrest-row-${row.restaurant_id}`}
                className="border-b last:border-b-0"
              >
                {/* Restaurant cell — tinted */}
                <td className="bg-slate-50 px-3 py-2 align-top">
                  <div className="font-semibold">{lib.name}</div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <Switch
                      checked={!!row.chefs_special?.enabled}
                      onCheckedChange={(c) =>
                        patchRow(row.restaurant_id, {
                          chefs_special: { ...(row.chefs_special || {}), enabled: c },
                        })
                      }
                      data-testid={`regrest-chef-toggle-${row.restaurant_id}`}
                    />
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Chef's Special
                    </span>
                  </div>
                  <ChefSpecialControl
                    value={row.chefs_special}
                    onChange={(cs) =>
                      patchRow(row.restaurant_id, { chefs_special: cs })
                    }
                    specialsLib={chefsSpecialsLib}
                    chefsSpecialsGroupsLib={chefsSpecialsGroupsLib}
                    restaurantId={row.restaurant_id}
                    isDuplicate={chefDupInfo(row).isDup}
                    otherDupDays={chefDupInfo(row).otherDays}
                    sameDayCount={chefDupInfo(row).sameDayCount}
                  />
                </td>
                {hasBreakfastColumn && (
                  libHasBreakfast ? (
                    <SidePanel
                      row={row}
                      side="breakfast"
                      restaurant={lib}
                      onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                      isMenuDup={isRowMenuDuplicate(menuDuplicates, row, "breakfast", currentDayNumber).isDup}
                      otherDupDays={isRowMenuDuplicate(menuDuplicates, row, "breakfast", currentDayNumber).days}
                    />
                  ) : (
                    <td
                      className="border-l bg-gray-50/60 px-3 py-2 align-top text-[10px] text-muted-foreground"
                      data-testid={`regrest-breakfast-na-${row.restaurant_id}`}
                    >
                      <span className="italic">— No breakfast service —</span>
                    </td>
                  )
                )}
                {/* Phase 13 — Embarkation Breakfast (Day 1 only). Reuses
                    the regular breakfast menu rotation since it's the
                    same restaurant serving at a different time slot. */}
                {isEmbarkDay && (
                  <SidePanel
                    row={row}
                    side="embarkation_breakfast"
                    restaurant={lib}
                    onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                    isMenuDup={false}
                    otherDupDays={[]}
                    menuListKey="breakfast"
                    dessertListKey="breakfast"
                    label="Embarkation Breakfast"
                  />
                )}
                {hasBrunchColumn && (
                  (lib.brunch_menus || []).length > 0 ? (
                    <SidePanel
                      row={row}
                      side="brunch"
                      restaurant={lib}
                      onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                      isMenuDup={isRowMenuDuplicate(menuDuplicates, row, "brunch", currentDayNumber).isDup}
                      otherDupDays={isRowMenuDuplicate(menuDuplicates, row, "brunch", currentDayNumber).days}
                    />
                  ) : (
                    <td
                      className="border-l bg-sky-50/30 px-3 py-2 align-top text-[10px] text-muted-foreground"
                      data-testid={`regrest-brunch-na-${row.restaurant_id}`}
                    >
                      <span className="italic">— No brunch service —</span>
                    </td>
                  )
                )}
                <SidePanel
                  row={row}
                  side="lunch"
                  restaurant={lib}
                  onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                  isMenuDup={isRowMenuDuplicate(menuDuplicates, row, "lunch", currentDayNumber).isDup}
                  otherDupDays={isRowMenuDuplicate(menuDuplicates, row, "lunch", currentDayNumber).days}
                />
                {isEmbarkDay && (
                  <SidePanel
                    row={row}
                    side="embarkation_lunch"
                    restaurant={lib}
                    onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                    isMenuDup={false}
                    otherDupDays={[]}
                    menuListKey="lunch"
                    dessertListKey="lunch"
                    label="Embarkation Lunch"
                  />
                )}
                {hasTeaTimeColumn && (
                  (lib.tea_time_menus || []).length > 0 ? (
                    <SidePanel
                      row={row}
                      side="tea_time"
                      restaurant={lib}
                      onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                      isMenuDup={isRowMenuDuplicate(menuDuplicates, row, "tea_time", currentDayNumber).isDup}
                      otherDupDays={isRowMenuDuplicate(menuDuplicates, row, "tea_time", currentDayNumber).days}
                    />
                  ) : (
                    <td
                      className="border-l bg-emerald-50/30 px-3 py-2 align-top text-[10px] text-muted-foreground"
                      data-testid={`regrest-tea-time-na-${row.restaurant_id}`}
                    >
                      <span className="italic">— No tea time service —</span>
                    </td>
                  )
                )}
                {/* Phase 13 — Embarkation Dinner (Day 1 only). Reuses
                    the regular dinner menu rotation. */}
                {isEmbarkDay && (
                  <SidePanel
                    row={row}
                    side="embarkation_dinner"
                    restaurant={lib}
                    onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                    isMenuDup={false}
                    otherDupDays={[]}
                    menuListKey="dinner"
                    dessertListKey="dinner"
                    label="Embarkation Dinner"
                  />
                )}
                <SidePanel
                  row={row}
                  side="dinner"
                  restaurant={lib}
                  onPatch={(patch) => patchRow(row.restaurant_id, patch)}
                  isMenuDup={isRowMenuDuplicate(menuDuplicates, row, "dinner", currentDayNumber).isDup}
                  otherDupDays={isRowMenuDuplicate(menuDuplicates, row, "dinner", currentDayNumber).days}
                />
              </tr>,
              // Phase 7++ — venue-level Festivals editor row. Rendered
              // only when the lib doc opted in via `hosts_festivals=true`.
              // Spans the full table width (colSpan).
              lib.hosts_festivals ? (
                <tr
                  key={`${row.restaurant_id}-festivals`}
                  data-testid={`regrest-festivals-row-${row.restaurant_id}`}
                >
                  <td colSpan={colSpan} className="border-b bg-rose-50/20 px-3 py-2">
                    <FestivalsEditor
                      value={row.festivals || []}
                      onChange={(festivals) =>
                        patchRow(row.restaurant_id, { festivals })
                      }
                      festivalsLib={festivalsLib}
                      tidPrefix={`regrest-${row.restaurant_id}`}
                    />
                  </td>
                </tr>
              ) : null,
            ];
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={colSpan} className="px-3 py-8 text-center text-muted-foreground">
                No regular restaurants — add some in Settings → Restaurants.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
